/*
 * One of the team's own campaigns (owner, 2026-11-01, after a look at Mailchimp). The page reads top to bottom: its name
 * and switch, how it is doing, who gets it (rules with a live count), and the timeline, laid out like a text thread: each
 * step a day, sent by text, email or both, shown as the client will see it. Nothing changes until Save.
 */
import { useEffect, useState } from "react";
import {
  blankStep,
  customProblems,
  fillCustom,
  MAX_RULES,
  MAX_STEPS,
  RULE_INFO,
  RULE_KINDS,
  smsParts,
  type AudienceRule,
  type CustomBody,
  type CustomPage,
  type CustomStep,
  type RuleKind,
} from "@steward/shared";
import { useCustom, useCustomCount, useDeleteCustom, useSaveCustom } from "../api/campaigns";
import { useSession } from "../app/session";
import { InputText } from "../components/fields";
import { Icon } from "../components/Icon";
import { useToast } from "../components/overlays";
import { Banner, Button, Card, IconButton, Skeleton, StatusLabel, Switch } from "../components/parts";
import { errText } from "../components/work";

const SAMPLE = { first: "Sarah", name: "Sarah Lee" };
const HOURS = [9, 10, 11, 12, 13, 14, 15, 16, 17];
const hourWords = (h: number) => `${String(h > 12 ? h - 12 : h)} ${h >= 12 ? "PM" : "AM"}`;
const CHANNEL_WORDS: Record<CustomStep["channel"], string> = { sms: "Text", email: "Email", both: "Both" };

const bodyOf = (p: CustomPage): CustomBody => ({
  name: p.name,
  on: p.on,
  start: p.start,
  date: p.date,
  audience: p.audience,
  steps: p.steps,
});

export function CustomCampaign({ campaignKey, back }: { campaignKey: string; back: () => void }) {
  const { company, can } = useSession();
  const q = useCustom(company.id, campaignKey);
  const save = useSaveCustom();
  const drop = useDeleteCustom();
  const toast = useToast();
  const [f, setF] = useState<CustomBody | null>(null);
  useEffect(() => setF(null), [campaignKey]);
  if (!q.data) return <Skeleton rows={6} />;
  const p = q.data;
  const form = f ?? bodyOf(p);
  const owner = can("marketing.update");
  const dirty = f !== null && JSON.stringify(f) !== JSON.stringify(bodyOf(p));
  const problems = customProblems(form);
  const set = (v: Partial<CustomBody>) => setF({ ...form, ...v });
  const send = (body: CustomBody, msg: string) =>
    save.mutate(
      { key: campaignKey, body },
      {
        onSuccess: () => {
          toast(msg);
          setF(null);
        },
        onError: (e) => {
          toast(errText(e), { kind: "error" });
        },
      },
    );
  const flip = (on: boolean) => {
    if (on) {
      if (problems.length) return toast(problems[0]!, { kind: "error" });
      const when =
        form.start === "once"
          ? `starting ${form.date}`
          : "to each new person who fits the rules from today (the people who fit now don't get it)";
      if (!confirm(`Turn on ${form.name}? It goes to ${String(p.count.people)} people now, ${when}.`)) return;
    }
    send({ ...form, on }, on ? `${form.name} is on` : `${form.name} is off`);
  };
  const st = p.stats;
  return (
    <div className="stack">
      <div className="row camp-bar">
        <Button type="tertiary" icon="arrowLeft" onClick={back}>
          All campaigns
        </Button>
        {owner ? (
          <Button
            type="tertiary"
            size="small"
            icon="trash"
            loading={drop.isPending}
            onClick={() => {
              if (!confirm(`Delete ${p.name || "this campaign"}? Nobody hears anything, and its counts go with it.`)) return;
              drop.mutate(campaignKey, {
                onSuccess: () => {
                  toast("Campaign deleted");
                  back();
                },
              });
            }}
          >
            Delete
          </Button>
        ) : null}
      </div>
      <Card title={p.name || "New campaign"} action={<OnLabel on={p.on} />}>
        <fieldset className="stack cc-plain" disabled={!owner}>
          <InputText label="Campaign name" maxLength={60} value={form.name} onChange={(e) => set({ name: e.target.value })} />
          <p className="muted">Only you and your team see the name. Clients never do.</p>
          <label className="row camp-switch">
            <Switch checked={p.on} disabled={!owner || save.isPending} label="Send these automatically" onChange={flip} />
            <span>Send these automatically</span>
          </label>
          {p.on && dirty ? <p className="muted">It stays on while you change it. Changes count from the next send.</p> : null}
          {!p.on && problems.length ? (
            <div className="cc-todo">
              <span className="muted">Before it can go on</span>
              <ul>
                {problems.map((x) => (
                  <li key={x}>{x}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </fieldset>
      </Card>
      {(st.sent ?? 0) + (st.mailed ?? 0) > 0 ? (
        <div className="tiles">
          {(
            [
              ["Texted", st.sent],
              ["Emailed", st.mailed],
              ["Replied", st.replied],
              ["Opted out", st.stopped],
              ["Booked", st.booked],
              ["Still going", p.people.going],
            ] as const
          ).map(([label, v]) => (
            <div key={label} className="tile">
              <span className="value">{v ?? 0}</span>
              <span className="label">{label}</span>
            </div>
          ))}
        </div>
      ) : null}
      <Who page={p} form={form} set={set} owner={owner} />
      <Timeline page={p} form={form} set={set} owner={owner} />
      <Card title="How it stays safe">
        <ul className="camp-safe">
          <li>Weekdays 9 AM to 6 PM, a few at a time.</li>
          <li>It stops for anyone who replies, books new work, or opts out.</li>
          <li>Nobody gets two campaign texts in the same week.</li>
          <li>Each person goes through it once.</li>
          <li>Every email has an unsubscribe link. The first text anyone gets says how to opt out.</li>
        </ul>
      </Card>
      {owner ? (
        <div className="cc-save">
          {dirty && problems.length && p.on ? <Banner type="warning">{problems[0]}</Banner> : null}
          <div className="row end">
            {dirty ? (
              <Button type="tertiary" onClick={() => setF(null)}>
                Undo changes
              </Button>
            ) : null}
            <Button
              loading={save.isPending}
              disabled={!dirty}
              onClick={() => send(p.on && problems.length ? { ...form, on: false } : form, "Saved")}
            >
              Save
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function OnLabel({ on }: { on: boolean }) {
  return <StatusLabel status={on ? "success" : "inactive"}>{on ? "On" : "Off"}</StatusLabel>;
}

/* ---------- Who gets it ---------- */

function Who({
  page,
  form,
  set,
  owner,
}: {
  page: CustomPage;
  form: CustomBody;
  set: (v: Partial<CustomBody>) => void;
  owner: boolean;
}) {
  const { company } = useSession();
  const live = useCustomCount(company.id, form.audience);
  const [list, setList] = useState(false);
  const c = live.data ?? page.count;
  const rules = form.audience.rules;
  const put = (i: number, r: AudienceRule) =>
    set({ audience: { ...form.audience, rules: rules.map((x, j) => (j === i ? r : x)) } });
  return (
    <Card title="Who gets it">
      <fieldset className="stack cc-plain" disabled={!owner}>
        {rules.length > 1 ? (
          <label className="row cc-match">
            <span>Clients who fit</span>
            <select
              value={form.audience.match}
              onChange={(e) => set({ audience: { ...form.audience, match: e.target.value as "all" | "any" } })}
            >
              <option value="all">all of these</option>
              <option value="any">any of these</option>
            </select>
          </label>
        ) : null}
        {rules.length === 0 ? <p className="muted">No rules yet, so every client who may get marketing gets it.</p> : null}
        <ul className="cc-rules">
          {rules.map((r, i) => (
            <li key={i}>
              <Rule rule={r} page={page} onChange={(x) => put(i, x)} />
              <IconButton
                icon="close"
                label="Remove this rule"
                onClick={() => set({ audience: { ...form.audience, rules: rules.filter((_, j) => j !== i) } })}
              />
            </li>
          ))}
        </ul>
        {rules.length < MAX_RULES ? (
          <div>
            <Button
              type="tertiary"
              size="small"
              icon="plus"
              onClick={() =>
                set({ audience: { ...form.audience, rules: [...rules, { kind: "service", op: "had", value: "", n: 0 }] } })
              }
            >
              Add rule
            </Button>
          </div>
        ) : null}
        <div className="cc-count">
          <b>
            {c.people} {c.people === 1 ? "person" : "people"}
          </b>
          <span className="muted">{c.sms} by text</span>
          <span className="muted">{c.email} by email</span>
          {c.people > 0 ? (
            <Button type="tertiary" size="small" onClick={() => setList(!list)}>
              {list ? "Hide the list" : "See who"}
            </Button>
          ) : null}
        </div>
        {list ? (
          <ul className="cc-names">
            {c.names.map((n, i) => (
              <li key={i}>
                <span>{n.name}</span>
                <span className="muted">{[n.sms ? "text" : "", n.email ? "email" : ""].filter(Boolean).join(" and ")}</span>
              </li>
            ))}
            {c.people > c.names.length ? <li className="muted">and {c.people - c.names.length} more</li> : null}
          </ul>
        ) : null}
        <p className="muted small">Never goes to people who said no to marketing, opted out, owe money, complained, or moved.</p>
      </fieldset>
    </Card>
  );
}

function Rule({ rule, page, onChange }: { rule: AudienceRule; page: CustomPage; onChange: (r: AudienceRule) => void }) {
  const info = RULE_INFO[rule.kind];
  const picks: Partial<Record<RuleKind, string[]>> = {
    tag: page.pickers.tags,
    service: page.pickers.services,
    source: page.pickers.sources,
    city: page.pickers.cities,
  };
  const showN =
    info.n && !(rule.kind === "service" && rule.op !== "notSince") && !(rule.kind === "lastVisit" && rule.op === "none");
  return (
    <div className="row wrap cc-rule">
      <select
        aria-label="Rule"
        value={rule.kind}
        onChange={(e) => {
          const kind = e.target.value as RuleKind;
          onChange({ kind, op: RULE_INFO[kind].ops[0]!.op, value: "", n: 0 });
        }}
      >
        {RULE_KINDS.map((k) => (
          <option key={k} value={k}>
            {RULE_INFO[k].label}
          </option>
        ))}
      </select>
      <select aria-label="Choice" value={rule.op} onChange={(e) => onChange({ ...rule, op: e.target.value })}>
        {info.ops.map((o) => (
          <option key={o.op} value={o.op}>
            {o.label}
          </option>
        ))}
      </select>
      {info.value === "pick" ? (
        <select aria-label={info.label} value={rule.value} onChange={(e) => onChange({ ...rule, value: e.target.value })}>
          <option value="">Pick one</option>
          {(picks[rule.kind] ?? []).map((v) => (
            <option key={v}>{v}</option>
          ))}
          {rule.value && !(picks[rule.kind] ?? []).includes(rule.value) ? <option>{rule.value}</option> : null}
        </select>
      ) : info.value === "text" ? (
        <input
          aria-label={info.label}
          placeholder="70005, 70001"
          value={rule.value}
          maxLength={200}
          onChange={(e) => onChange({ ...rule, value: e.target.value })}
        />
      ) : null}
      {showN ? (
        <span className="row cc-n">
          <input
            type="number"
            aria-label={info.n}
            min={0}
            value={rule.n}
            onChange={(e) => onChange({ ...rule, n: Math.max(0, Math.round(Number(e.target.value) || 0)) })}
          />
          {rule.kind === "paid" ? null : <span className="muted">{info.n}</span>}
          {(rule.kind === "lastVisit" || rule.kind === "since") && rule.op === "before" ? (
            <span className="muted">ago</span>
          ) : null}
        </span>
      ) : null}
    </div>
  );
}

/* ---------- The timeline ---------- */

function Timeline({
  page,
  form,
  set,
  owner,
}: {
  page: CustomPage;
  form: CustomBody;
  set: (v: Partial<CustomBody>) => void;
  owner: boolean;
}) {
  const steps = form.steps;
  const put = (i: number, s: CustomStep) => set({ steps: steps.map((x, j) => (j === i ? s : x)) });
  const who = { ...SAMPLE, business: page.business, service: page.pickers.services[0] ?? "" };
  return (
    <Card title="Timeline">
      <fieldset className="stack cc-plain" disabled={!owner}>
        <div className="row wrap cc-start">
          <label className="field">
            <span className="label">Who starts and when</span>
            <select value={form.start} onChange={(e) => set({ start: e.target.value as CustomBody["start"] })}>
              <option value="once">Everyone who fits, on one day</option>
              <option value="joins">Each person on the day they come to fit</option>
            </select>
          </label>
          {form.start === "once" ? (
            <InputText label="Day 0 is" type="date" value={form.date} onChange={(e) => set({ date: e.target.value })} />
          ) : null}
        </div>
        <p className="muted">
          {form.start === "once"
            ? "Everyone who fits the rules on that day starts together."
            : "Only people who come to fit the rules after it's on start it, like a client whose last visit just passed 6 months."}
        </p>
        <ol className="cc-line">
          {steps.map((s, i) => {
            const gap = i > 0 ? s.day - steps[i - 1]!.day : 0;
            return (
              <li key={s.id}>
                {i > 0 ? (
                  <div className="cc-wait">
                    <Icon name="clock" size={16} />
                    {gap > 0 ? `Wait ${String(gap)} ${gap === 1 ? "day" : "days"}` : "Same day as the step before"}
                  </div>
                ) : null}
                <Step
                  step={s}
                  who={who}
                  optout={i === 0 ? page.optout : ""}
                  canRemove={steps.length > 1}
                  onChange={(x) => put(i, x)}
                  onRemove={() => set({ steps: steps.filter((_, j) => j !== i) })}
                />
              </li>
            );
          })}
        </ol>
        {steps.length < MAX_STEPS ? (
          <div>
            <Button
              type="tertiary"
              size="small"
              icon="plus"
              onClick={() => set({ steps: [...steps, blankStep((steps[steps.length - 1]?.day ?? -3) + 3)] })}
            >
              Add step
            </Button>
          </div>
        ) : null}
      </fieldset>
    </Card>
  );
}

function Step({
  step: s,
  who,
  optout,
  canRemove,
  onChange,
  onRemove,
}: {
  step: CustomStep;
  who: { first: string; name: string; business: string; service: string };
  optout: string;
  canRemove: boolean;
  onChange: (s: CustomStep) => void;
  onRemove: () => void;
}) {
  const set = (v: Partial<CustomStep>) => onChange({ ...s, ...v });
  const text = s.channel !== "email" || s.fallback;
  const mail = s.channel !== "sms" || s.fallback;
  const sample = fillCustom(s.text, who).trim();
  const shown = sample && optout ? `${sample} ${optout}` : sample;
  return (
    <div className="cc-step">
      <span className="cc-dot" aria-hidden>
        <Icon name={s.channel === "email" ? "mail" : "message"} size={16} />
      </span>
      <div className="stack cc-step-body">
        <div className="row wrap cc-step-head">
          <label className="row cc-day">
            <b>Day</b>
            <input
              type="number"
              aria-label="Day"
              min={0}
              max={365}
              value={s.day}
              onChange={(e) => set({ day: Math.max(0, Math.min(365, Math.round(Number(e.target.value) || 0))) })}
            />
          </label>
          <label className="row cc-day">
            <span className="muted">from</span>
            <select aria-label="Send from" value={s.hour} onChange={(e) => set({ hour: Number(e.target.value) })}>
              {HOURS.map((h) => (
                <option key={h} value={h}>
                  {hourWords(h)}
                </option>
              ))}
            </select>
          </label>
          <span className="cc-chips" role="radiogroup" aria-label="Send by">
            {(["sms", "email", "both"] as const).map((c) => (
              <button
                key={c}
                type="button"
                role="radio"
                aria-checked={s.channel === c}
                className={s.channel === c ? "on" : ""}
                onClick={() => set({ channel: c })}
              >
                {CHANNEL_WORDS[c]}
              </button>
            ))}
          </span>
          {canRemove ? <IconButton icon="trash" label="Remove this step" onClick={onRemove} /> : null}
        </div>
        {s.channel === "both" ? (
          <label className="row cc-opt">
            <select value={s.both} onChange={(e) => set({ both: e.target.value as CustomStep["both"] })}>
              <option value="prefers">Only the way each client likes to hear from you</option>
              <option value="both">Send both the text and the email</option>
            </select>
          </label>
        ) : (
          <label className="row cc-opt">
            <input type="checkbox" checked={s.fallback} onChange={(e) => set({ fallback: e.target.checked })} />
            <span>{s.channel === "sms" ? "No mobile number? Send an email instead" : "No email? Send a text instead"}</span>
          </label>
        )}
        {text ? (
          <div className="stack cc-part">
            <label className="field wide">
              <span className="label">{s.channel === "email" ? "Text, if there's no email" : "Text"}</span>
              <textarea rows={3} maxLength={480} value={s.text} onChange={(e) => set({ text: e.target.value })} />
            </label>
            <span className="muted small">
              Fill-ins {"{first} {name} {business} {service}"} · {shown.length} letters, {smsParts(shown)}{" "}
              {smsParts(shown) === 1 ? "text" : "texts"}
            </span>
            {sample ? (
              <div className="bubble cc-bubble">
                <p>{shown}</p>
              </div>
            ) : null}
          </div>
        ) : null}
        {mail ? (
          <div className="stack cc-part">
            <InputText
              label={s.channel === "sms" ? "Email subject, if there's no mobile" : "Email subject"}
              maxLength={120}
              value={s.subject}
              onChange={(e) => set({ subject: e.target.value })}
            />
            <label className="field wide">
              <span className="label">Email words</span>
              <textarea rows={5} maxLength={3000} value={s.body} onChange={(e) => set({ body: e.target.value })} />
            </label>
            <div className="row wrap">
              <InputText
                label="Button words (optional)"
                maxLength={40}
                value={s.button}
                onChange={(e) => set({ button: e.target.value })}
              />
              {s.button.trim() ? (
                <InputText
                  label="Button link"
                  placeholder="https://"
                  maxLength={300}
                  value={s.link}
                  onChange={(e) => set({ link: e.target.value })}
                />
              ) : null}
            </div>
            {s.subject.trim() || s.body.trim() ? (
              <div className="camp-mail cc-mail">
                <b>{fillCustom(s.subject, who)}</b>
                <p>{fillCustom(s.body, who)}</p>
                {s.button.trim() ? (
                  <span className="btn primary small" aria-hidden>
                    {s.button}
                  </span>
                ) : null}
                <span className="muted small">Unsubscribe link and your address go at the bottom.</span>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
